'use client';

import { useEffect, useRef, useState } from 'react';

const faqs = [
  {
    q: 'Is Cartelix LLC a legitimate registered US company?',
    a: 'Yes. Cartelix LLC is a legally registered limited liability company in Wyoming, USA. Our registered business address is 30 N Gould St Ste R, Sheridan, WY 82801. We operate under US business law and are fully accountable under Wyoming state regulations.',
  },
  {
    q: 'How does the 50/50 payment model work?',
    a: 'Before we begin any project, we require a 50% deposit of the agreed project total. This confirms your commitment and allows us to allocate resources. The remaining 50% is due once you have reviewed and approved the final deliverables. We never request full payment upfront.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept Visa, Mastercard, American Express, Discover, Apple Pay, and Google Pay via Stripe, as well as PayPal. All payments are processed in USD. Our PayPal business email is contact@cartelix-llc.com.',
  },
  {
    q: 'Do you work with international clients?',
    a: 'Absolutely. While we are a US-registered agency, the majority of our 240+ completed projects have been for clients outside the United States — across Europe, the Middle East, Southeast Asia, and Latin America. We communicate primarily in English and operate across time zones.',
  },
  {
    q: 'What is your refund policy?',
    a: 'We offer a 14-day refund window from the date of deposit, provided no significant work has commenced. If project development is already underway, a partial refund may be issued based on work completed. Completed deliverables that have been approved are non-refundable. Full details are in our Refund Policy.',
  },
  {
    q: 'How long does a typical project take?',
    a: 'Timelines vary by project type and complexity. A standard website typically takes 2–4 weeks. Mobile applications take 6–12 weeks. Social media content packages are delivered on a monthly cycle. Rebranding projects average 2–3 weeks. Exact timelines are specified in your proposal.',
  },
  {
    q: 'Who owns the finished work?',
    a: 'Upon receipt of final payment, you own 100% of all delivered work — including all source files, design assets, code, and documentation. We retain no license to use your deliverables without explicit written permission.',
  },
  {
    q: 'How do I get started?',
    a: 'Simply fill out the contact form on this page or email us at contact@cartelix-llc.com. Describe your project briefly and we will respond within 1 business day to schedule a free 30-minute consultation call.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const answerRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('is-visible'); }),
      { threshold: 0.1 }
    );
    sectionRef.current?.querySelectorAll('.reveal-hidden').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const toggle = (idx: number) => {
    setOpenIndex(openIndex === idx ? null : idx);
  };

  return (
    <section id="faq" ref={sectionRef} className="py-24 lg:py-32" style={{ background: '#F8FAFF' }}>
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14 reveal-hidden">
          <span className="section-label">FAQ</span>
          <div className="section-divider mx-auto" />
          <h2
            className="font-heading font-extrabold leading-tight mb-4"
            style={{ fontSize: 'clamp(2rem, 3.5vw, 2.8rem)', color: '#0D1A3A', letterSpacing: '-0.025em' }}
          >
            Frequently Asked<br />Questions
          </h2>
          <p className="text-base" style={{ color: '#6B7280' }}>
            Everything you need to know about working with Cartelix LLC.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-3 reveal-hidden">
          {faqs.map((faq, idx) => (
            <div key={idx} className={`faq-item ${openIndex === idx ? 'open' : ''}`}>
              <button className="faq-trigger" onClick={() => toggle(idx)}>
                <span>{faq.q}</span>
                <div className="faq-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1F3A8A" strokeWidth="2.5" strokeLinecap="round">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                </div>
              </button>
              <div
                ref={(el) => { answerRefs.current[idx] = el; }}
                className="faq-answer"
                style={{ maxHeight: openIndex === idx ? '400px' : '0' }}
              >
                <div className="faq-answer-inner">{faq.a}</div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12 reveal-hidden">
          <p className="text-sm mb-4" style={{ color: '#6B7280' }}>
            Still have questions? We&apos;re happy to help.
          </p>
          <a href="#contact" className="btn-primary">
            Contact Our Team
          </a>
        </div>
      </div>
    </section>
  );
}